/**
 * Assembles GLSL sources with their dependencies. This module's only job is
 * the ASSEMBLY map at the bottom: which shaders get which dependency chunks.
 *
 * Dependencies are spliced after the host's #version and leading precision
 * statements; a #line directive re-anchors driver error line numbers to the
 * original file. Dependency chunks must not declare precision (the host owns
 * it) -- any that slip in are stripped.
 */
import { EDGE_STRIP_SEGMENTS } from '../components/scene/edge-instances.ts';
import edgesdfCommon from '!raw-loader!@graphistry/viz/src/shaders/edgesdf.common.glsl';
import edgesdfCommonVertex from '!raw-loader!@graphistry/viz/src/shaders/edgesdf.common.vertex.glsl';
import edgesdfCommonFragment from '!raw-loader!@graphistry/viz/src/shaders/edgesdf.common.fragment.glsl';
import edgesdfVertex from '!raw-loader!glslify-loader!@graphistry/viz/src/shaders/edgesdf.vertex.glsl';
import edgesdfpickingVertex from '!raw-loader!glslify-loader!@graphistry/viz/src/shaders/edgesdfpicking.vertex.glsl';
import edgesdfarrowVertex from '!raw-loader!glslify-loader!@graphistry/viz/src/shaders/edgesdfarrow.vertex.glsl';
import edgesdfFragment from '!raw-loader!glslify-loader!@graphistry/viz/src/shaders/edgesdf.fragment.glsl';
import edgesdfpickingFragment from '!raw-loader!glslify-loader!@graphistry/viz/src/shaders/edgesdfpicking.fragment.glsl';
import nodeCommonVertex from '!raw-loader!@graphistry/viz/src/shaders/node.common.vertex.glsl';
import pointculledVertex from '!raw-loader!glslify-loader!@graphistry/viz/src/shaders/pointculled.vertex.glsl';
import pointpickingVertex from '!raw-loader!glslify-loader!@graphistry/viz/src/shaders/pointpicking.vertex.glsl';
import pointselectedVertex from '!raw-loader!glslify-loader!@graphistry/viz/src/shaders/pointselected.vertex.glsl';
import pointhighlightVertex from '!raw-loader!glslify-loader!@graphistry/viz/src/shaders/pointhighlighted.vertex.glsl';

//defines whose source of truth is TypeScript -- generated, never hand-synced.
//STRIP_SEGMENTS also determines the items' verticesPerInstance (items.js
//derives it from the same constant). Pure-GLSL constants live in
//edgesdf.common.glsl instead.
const GENERATED_DEFINES = `#define STRIP_SEGMENTS ${EDGE_STRIP_SEGMENTS}.0`;

const stripPrecision = (dep: string): string =>
    dep.replace(/^[ \t]*precision[ \t]+\w+[ \t]+\w+[ \t]*;.*$/gm, '');

function assemble(source: string, deps: string[]): string {
    const lines = source.split('\n');
    if (lines[0].indexOf('#version') !== 0) {
        throw new Error('glsl.ts assemble: #version must be the first line');
    }
    let at = 1;
    while (at < lines.length && /^\s*precision\s/.test(lines[at])) {
        at++;
    }
    return lines.slice(0, at).join('\n') + '\n' +
        deps.map(stripPrecision).join('\n') + '\n' +
        `#line ${at + 1}\n` +
        lines.slice(at).join('\n');
}

const EDGESDF_VERTEX_DEPS = [GENERATED_DEFINES, edgesdfCommon, edgesdfCommonVertex];
const EDGESDF_FRAGMENT_DEPS = [GENERATED_DEFINES, edgesdfCommon, edgesdfCommonFragment];
const POINT_VERTEX_DEPS = [nodeCommonVertex];

export default {
    edgesdfVertex: assemble(edgesdfVertex, EDGESDF_VERTEX_DEPS),
    edgesdfpickingVertex: assemble(edgesdfpickingVertex, EDGESDF_VERTEX_DEPS),
    edgesdfarrowVertex: assemble(edgesdfarrowVertex, EDGESDF_VERTEX_DEPS),
    edgesdfFragment: assemble(edgesdfFragment, EDGESDF_FRAGMENT_DEPS),
    edgesdfpickingFragment: assemble(edgesdfpickingFragment, EDGESDF_FRAGMENT_DEPS),
    pointculledVertex: assemble(pointculledVertex, POINT_VERTEX_DEPS),
    pointpickingVertex: assemble(pointpickingVertex, POINT_VERTEX_DEPS),
    pointselectedVertex: assemble(pointselectedVertex, POINT_VERTEX_DEPS),
    pointhighlightVertex: assemble(pointhighlightVertex, POINT_VERTEX_DEPS),
};
